// Marquee-to-add: a pointer drag starting on OPEN grid cells sweeps out a
// snapped, obstacle-aware rect (see `marqueeArea`) and hands the settled area
// to the consumer on release — the consumer opens its inline picker there.
// Desktop-only (mouse/pen; mobile is singleColumn anyway).
//
// A first-class gesture: arming calls `setGesture({ mode: 'marquee', … })`, so
// the row floor, cell overlay, and canManage gating all engage through the
// normal transition with zero special-casing. The marquee never commits a
// layout — `cancelGesture` on release, then `onAreaSelect(area)`.
//
// Pointer-capture discipline mirrors the resize hook: everything lives in
// closures per gesture, window listeners self-clean, and the scroll parent's
// displacement since gesture start folds into the pointer point so cell math
// stays in grid-content space while edge auto-scroll moves the viewport.

import * as React from 'react';

import { createEdgeAutoScroller, type EdgeAutoScroller } from './edge-auto-scroll';
import { MARQUEE_SURFACE_ATTR } from './grid-dom-markers';
import { getScrollParent, pointToCell, rowCapacity } from './grid-geometry';
import { layoutBottom, marqueeArea, type GridRect } from './grid-layout';
import { MARQUEE_ID, type GridGestureCore } from './grid-gesture';

/** Matches dnd-kit's MouseSensor activation distance, so a marquee and a tile
 *  drag arm at the same travel. */
const ACTIVATION_DISTANCE_PX = 4;

/** Interactive descendants own their presses — a pointer landing on any of
 *  these inside a marquee surface must never arm the marquee. */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [contenteditable]';

export interface GridMarqueeOptions {
  enabled: boolean;
  onAreaSelect: (area: GridRect) => void;
}

export function useGridMarquee(
  core: GridGestureCore,
  options: GridMarqueeOptions,
): { onPointerDown: (event: React.PointerEvent) => void } {
  // Stable-event pattern: the handler stays referentially stable and reads
  // the latest consumer options through this ref.
  const optionsRef = React.useRef(options);
  React.useLayoutEffect(() => {
    optionsRef.current = options;
  });

  // Unmount teardown: the active gesture's cleanup (window listeners, rAF
  // scroller, capture, userSelect) must run even when the grid unmounts
  // mid-sweep. Set on arm, nulled inside cleanup itself.
  const activeCleanupRef = React.useRef<(() => void) | null>(null);
  React.useEffect(() => () => activeCleanupRef.current?.(), []);

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      if (!optionsRef.current.enabled) return;
      if (event.button !== 0) return;
      // Desktop-only v1: mouse and pen arm, touch never does (mobile renders
      // the singleColumn branch anyway).
      if (event.pointerType === 'touch') return;
      // Another gesture (drag/resize/external) owns the pointer.
      if (core.gestureRef.current) return;
      const gridEl = core.gridEl();
      if (!gridEl) return;

      // Surface test: only the grid element itself (empty cells — tiles and
      // every preview layer are descendants or pointer-transparent) or an
      // opted-in marquee-surface band inside this grid arms the marquee. A
      // nested grid's empty cells hit the NESTED grid element, so the outer
      // marquee is naturally excluded there. Interactive descendants inside
      // a surface (the gutter "+" buttons) own their presses outright.
      const target = event.target as HTMLElement | null;
      const surface =
        target === gridEl
          ? gridEl
          : (target?.closest(`[${MARQUEE_SURFACE_ATTR}]`) ?? null);
      if (!surface || !gridEl.contains(surface)) return;
      const interactive = target?.closest(INTERACTIVE_SELECTOR) ?? null;
      if (interactive && surface.contains(interactive)) return;

      const startClient = { x: event.clientX, y: event.clientY };
      const startRect = gridEl.getBoundingClientRect();
      const scrollParent = getScrollParent(gridEl);
      const startScrollTop = scrollParent?.scrollTop ?? 0;
      const pointerId = event.pointerId;
      // NO capture and NO preventDefault here — a <4px release must stay a
      // plain compatibility click (builder deselect, quick-add "+" presses),
      // and Chromium retargets the compat click to the capturing element.
      // Capture is taken inside `arm()` (the pointer is still active at ≥4px
      // travel; capture is legal from any handler while the pointer is down).

      let selecting = false;
      let anchor: { x: number; y: number } | null = null;
      let floorRows = 1;
      let lastCell: { x: number; y: number } | null = null;
      let scroller: EdgeAutoScroller | null = null;
      const lastPointer = { x: startClient.x, y: startClient.y };

      const applyPointer = () => {
        if (!selecting || !anchor) return;
        const metrics = core.scratch.current.metrics;
        if (!metrics) return;
        // Content-space point: client − grid origin at start + the scroll
        // fold (the grid element itself never moves within its scroll parent
        // mid-marquee — the gesture adds no rows past the floor).
        const scrollDelta = (scrollParent?.scrollTop ?? 0) - startScrollTop;
        const point = {
          x: lastPointer.x - startRect.left,
          y: lastPointer.y - startRect.top + scrollDelta,
        };
        const cell = pointToCell(metrics, core.columns, floorRows, point);
        // Render cadence: only a snapped-cell change recomputes the area.
        if (lastCell && lastCell.x === cell.x && lastCell.y === cell.y) return;
        lastCell = cell;
        core.setArea(
          marqueeArea(core.layoutRef.current, anchor, cell, core.columns),
        );
      };

      // Native text selection must never ride the sweep. `preventDefault` on
      // the pointerdown would stop it too, but that kills the <4px
      // compatibility click (see above) — so block the selection at its own
      // source instead: `selectstart` fires for any selection THIS press
      // tries to start, and preventing it leaves clicks untouched. Lives for
      // the whole press (the sweep crosses text-bearing tiles long before
      // `arm()`'s userSelect lock could catch up).
      const suppressSelect = (e: Event) => e.preventDefault();
      document.addEventListener('selectstart', suppressSelect);

      const cleanup = () => {
        activeCleanupRef.current = null;
        scroller?.stop();
        scroller = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onPointerCancel);
        window.removeEventListener('keydown', onKeyDown);
        window.removeEventListener('blur', onWindowCancel);
        document.removeEventListener('selectstart', suppressSelect);
        document.documentElement.style.userSelect = '';
        try {
          gridEl.releasePointerCapture(pointerId);
        } catch {
          // capture may already be gone
        }
      };
      activeCleanupRef.current = cleanup;

      // One-shot capture-phase click suppressor: the pointerup that ends a
      // real marquee still produces a compatibility click on the grid, which
      // would immediately fire consumer click behavior (e.g. deselect) under
      // the just-opened picker.
      const suppressTrailingClick = () => {
        const suppress = (e: MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
        };
        gridEl.addEventListener('click', suppress, { capture: true, once: true });
        // Self-remove even if no click arrives (e.g. pointer released off-grid).
        window.setTimeout(() => {
          gridEl.removeEventListener('click', suppress, { capture: true });
        }, 0);
      };

      const arm = () => {
        const metrics = core.measure();
        if (!metrics) return false;
        const layout = core.layoutRef.current;
        core.scratch.current.metrics = metrics;
        // Same formula the shell uses for its row floor — the marquee clamps
        // its cells to the floor so the selection can't outgrow the canvas.
        floorRows = Math.max(
          1,
          Math.max(layoutBottom(layout), rowCapacity(startRect.height, metrics)),
        );
        const anchorCell = pointToCell(metrics, core.columns, floorRows, {
          x: startClient.x - startRect.left,
          y: startClient.y - startRect.top,
        });
        // Occupied anchor → nothing to select. Silent abort (the pointer
        // already moved past the click threshold, there is nothing to protect).
        const anchorArea = marqueeArea(layout, anchorCell, anchorCell, core.columns);
        if (anchorArea == null) return false;
        anchor = anchorCell;
        lastCell = { ...anchorCell };
        selecting = true;
        // Capture only once armed — sub-threshold presses stay ordinary
        // clicks with no retargeting.
        try {
          gridEl.setPointerCapture(pointerId);
        } catch {
          // pointer may already be released
        }
        document.documentElement.style.userSelect = 'none';
        scroller = createEdgeAutoScroller({ scrollParent, onScrolled: applyPointer });
        core.setGesture({
          id: MARQUEE_ID,
          mode: 'marquee',
          origin: null,
          preview: null,
          // The anchor rect WITH the anti-island shift applied — a sweep that
          // never leaves the anchor cell must deliver the same WYSIWYG rect a
          // moving sweep would (`applyPointer` early-returns on an unchanged
          // cell, so this initial area is the one `onUp` delivers then).
          area: anchorArea,
        });
        return true;
      };

      const onMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) return;
        lastPointer.x = move.clientX;
        lastPointer.y = move.clientY;
        if (!selecting) {
          if (
            Math.hypot(
              move.clientX - startClient.x,
              move.clientY - startClient.y,
            ) < ACTIVATION_DISTANCE_PX
          ) {
            return;
          }
          if (!arm()) {
            cleanup();
            return;
          }
        }
        move.preventDefault();
        scroller?.update(move.clientY);
        applyPointer();
      };

      const onUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) return;
        const wasSelecting = selecting;
        const gesture = core.gestureRef.current;
        const area = gesture?.mode === 'marquee' ? gesture.area : null;
        cleanup();
        // A <4px release never armed: pass the plain click through untouched.
        if (!wasSelecting) return;
        // The marquee never commits a layout — clear, then hand off the area.
        // Only clear a gesture that is still OURS (self-contained even
        // without the entry-point exclusivity guards).
        if (gesture?.mode === 'marquee') core.cancelGesture();
        suppressTrailingClick();
        if (area) optionsRef.current.onAreaSelect(area);
      };

      // Escape/blur cancel by design regardless of pointer; pointercancel
      // only for OUR pointer.
      const onWindowCancel = () => {
        const wasSelecting = selecting;
        cleanup();
        if (!wasSelecting) return;
        if (core.gestureRef.current?.mode === 'marquee') core.cancelGesture();
      };
      const onPointerCancel = (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        onWindowCancel();
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onWindowCancel();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onPointerCancel);
      window.addEventListener('keydown', onKeyDown);
      window.addEventListener('blur', onWindowCancel);
    },
    [core],
  );

  return React.useMemo(() => ({ onPointerDown }), [onPointerDown]);
}
